import { useState } from 'react'
import {
  ACKNOWLEDGE_CONFIRMATION, kindLabel, ownersSentence, statusSentence,
} from '../sharing/ipModel'
import type { IpArrangement } from '../../types/ipArrangements'

/**
 * The "IP arrangement" card in the invention rail (figure S3).
 *
 * An arrangement records that an option, a licence or an assignment was agreed
 * OUTSIDE InnovateFlow. It is a RECORD, NOT A DIAL: acknowledging one changes
 * nothing about who can see this invention. What anyone can see is decided by
 * the grants on the sharing grid — by the inventor — and this card never
 * touches them. That is why the card sits BELOW "Who can see this" rather than
 * inside it: the two answer different questions.
 *
 * The figure's card carries no reference line — the pointer to the signed
 * agreement is drawn on the sharing screen's card, not on this one — so
 * neither does this. The record's `reference` is read there.
 *
 * Every word here comes from the record the API returned. When nothing has
 * been proposed the card says so in the present tense and promises nothing —
 * an absent record means the inventor retains, and a card that hinted at a
 * future proposal would be inventing one.
 */
export interface IpArrangementCardProps {
  /** The live record for THIS invention, or null when nothing is on record. */
  arrangement: IpArrangement | null
  /** Real calls, injected by the rail — the card owns no fetching of its own. */
  onAcknowledge?: (arrangementId: string) => Promise<unknown>
  onDecline?: (arrangementId: string) => Promise<unknown>
}

/**
 * The card quotes the same sentence the acknowledgement itself records, in the
 * third person because "You will confirm:" already supplies the "I". Deriving
 * it from the one constant is what stops the promise on the card and the
 * promise in the dialog from drifting apart.
 */
const QUOTED = ACKNOWLEDGE_CONFIRMATION.replace(/^I confirm this/, 'This')

/** "12 Aug" — the figure's own short form, in Sydney time like every other date. */
function shortDate(iso: string | null): string | null {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', timeZone: 'Australia/Sydney' })
}

const ACTION =
  'rounded-lg border border-sky-200 bg-white px-3 py-1.5 text-[12px] font-bold text-iflow-700 '
  + 'hover:bg-sky-50 disabled:opacity-50'

export default function IpArrangementCard({
  arrangement, onAcknowledge, onDecline,
}: IpArrangementCardProps) {
  const [confirming, setConfirming] = useState(false)
  const [busy, setBusy] = useState<'acknowledge' | 'decline' | null>(null)
  const [error, setError] = useState<string | null>(null)

  const heading = (
    <h3 className="mb-2 text-[13px] font-bold text-iflow-900">
      <span aria-hidden>⚖</span> IP arrangement
    </h3>
  )

  if (!arrangement) {
    return (
      <section className="light-card p-4" data-testid="ip-arrangement-card">
        {heading}
        <p className="text-[11.5px] text-gray-500">
          No IP arrangement is on record for this invention.
        </p>
      </section>
    )
  }

  const when = shortDate(arrangement.proposed_at)
  const who = arrangement.proposed_by ?? 'an organisation'
  const waiting = arrangement.status === 'proposed' && arrangement.my_state === 'pending'
  const status = statusSentence(arrangement)

  async function run(action: 'acknowledge' | 'decline') {
    const fn = action === 'acknowledge' ? onAcknowledge : onDecline
    if (!fn || !arrangement) return
    setBusy(action)
    setError(null)
    try {
      await fn(arrangement.arrangement_id)
      setConfirming(false)
    } catch {
      setError(
        action === 'acknowledge'
          ? 'That acknowledgement did not save. Nothing was recorded — try again.'
          : 'That decline did not save. The proposal still stands — try again.',
      )
    } finally {
      setBusy(null)
    }
  }

  return (
    <section className="light-card p-4" data-testid="ip-arrangement-card">
      {heading}
      <p className="text-[12.5px] leading-relaxed text-iflow-900">
        <b>{kindLabel(arrangement.kind)}</b>
        {' · proposed by '}
        {who}
        {when ? `, ${when}` : ''}
        {waiting ? ` — ${ownersSentence(arrangement.owners_required)}.` : '.'}
      </p>
      {waiting ? (
        <p className="mt-2 text-[11.5px] leading-relaxed text-gray-500">
          You will confirm: “{QUOTED}” Declining ends the proposal for every owner.
        </p>
      ) : (
        status && <p className="mt-2 text-[11.5px] leading-relaxed text-gray-500">{status}</p>
      )}
      {waiting && (onAcknowledge || onDecline) && (
        confirming ? (
          <div className="mt-2.5">
            <p className="mb-2 text-[11.5px] leading-relaxed text-iflow-900">
              {ACKNOWLEDGE_CONFIRMATION}
            </p>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                className={ACTION}
                disabled={busy !== null}
                onClick={() => { void run('acknowledge') }}
              >
                {busy === 'acknowledge' ? 'Recording…' : 'Confirm'}
              </button>
              <button
                type="button"
                className={ACTION}
                disabled={busy !== null}
                onClick={() => { setConfirming(false); setError(null) }}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="mt-2.5 flex flex-wrap gap-2">
            {onAcknowledge && (
              <button type="button" className={ACTION} onClick={() => setConfirming(true)}>
                Acknowledge
              </button>
            )}
            {onDecline && (
              <button
                type="button"
                className={ACTION}
                disabled={busy !== null}
                onClick={() => { void run('decline') }}
              >
                {busy === 'decline' ? 'Declining…' : 'Decline'}
              </button>
            )}
          </div>
        )
      )}
      {error && <p className="mt-2 text-[11.5px] font-semibold text-red-600">{error}</p>}
    </section>
  )
}
